/**
 * 🔒 The organization's own exemption, which a Purchase Order's one-time tax
 * exempt form prints (decision 9600; T3428). Settings › Tax › Our Exemption
 * edits the four keys; the legal name and the address are the Organization's
 * own company's, read and never keyed again. 🔨 This is not a
 * `TaxExemptCertificate`: that record is a customer's, exempting what we sell.
 */
import { CERTIFICATE_FORM_TEMPLATES, type CertificateFillMap, type CertificateFormTemplate, type OneTimeExemptionReason } from '../records/certificateTemplates';
import { fill, t } from '../strings';
import { NO_SALES_TAX_STATES, US_STATE_NAMES, stateExemptionForm } from '../records/taxExemption';

/** The organization's sales tax license or exemption number per state, one line each: the two-letter code, then the number. */
export const EXEMPTION_NUMBERS_KEY = 'tax.exemptionNumbers';
/** The person who signs the organization's one-time form. */
export const EXEMPTION_SIGNER_NAME_KEY = 'tax.exemptionSignerName';
/** The signer's title, written beside their name. */
export const EXEMPTION_SIGNER_TITLE_KEY = 'tax.exemptionSignerTitle';
/** The reason a new order's form opens on; unset reads Resale. */
export const EXEMPTION_REASON_KEY = 'tax.exemptionReason';

/** The four keys Settings › Tax › Our Exemption edits, in the order the page draws them. */
export const OUR_EXEMPTION_KEYS = [EXEMPTION_NUMBERS_KEY, EXEMPTION_SIGNER_NAME_KEY, EXEMPTION_SIGNER_TITLE_KEY, EXEMPTION_REASON_KEY] as const;

/** The page that edits them. */
export const OUR_EXEMPTION_ROUTE = '/settings/tax/our-exemption';

/** The reasons the organization buys exempt for, from the multistate set. */
export const ONE_TIME_EXEMPTION_REASONS: readonly OneTimeExemptionReason[] = ['Resale', 'Manufacturing'];

export const DEFAULT_ONE_TIME_EXEMPTION_REASON: OneTimeExemptionReason = 'Resale';

/** The longest signer's name or title the form's box takes. */
const SIGNER_TEXT_LIMIT = 80;

export function isOneTimeExemptionReason(value: unknown): value is OneTimeExemptionReason {
  return typeof value === 'string' && (ONE_TIME_EXEMPTION_REASONS as readonly string[]).includes(value);
}

/** The reason a new order opens on: the stored default where it is one of the two, else Resale. */
export function defaultOneTimeExemptionReason(value: unknown): OneTimeExemptionReason {
  return isOneTimeExemptionReason(value) ? value : DEFAULT_ONE_TIME_EXEMPTION_REASON;
}

/** A state's name as a sentence reads it, or the code itself where the code is not a state. */
export function stateNameOf(stateCode: string): string {
  return US_STATE_NAMES[stateCode.toUpperCase()] ?? stateCode;
}

/** The stored lines as state code to number; a line that does not read is skipped. */
export function exemptionNumbers(value: unknown): ReadonlyMap<string, string> {
  const numbers = new Map<string, string>();
  if (!Array.isArray(value)) return numbers;
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const match = /^\s*([A-Za-z]{2})[\s:,-]+(\S.*?)\s*$/.exec(entry);
    if (match) numbers.set(match[1]!.toUpperCase(), match[2]!);
  }
  return numbers;
}

/** The organization's number in one state, or null. */
export function exemptionNumberFor(value: unknown, stateCode: string): string | null {
  return exemptionNumbers(value).get(stateCode.toUpperCase()) ?? null;
}

/** 🔒 The numbers' refusal: each line a state's two-letter code then a number, and no state twice. A blank clears the key. */
export function exemptionNumbersProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (!Array.isArray(value) || value.some((line) => typeof line !== 'string')) return t('po.exempt.numbersNotList');
  const seen = new Set<string>();
  for (const line of value as string[]) {
    const match = /^\s*([A-Za-z]{2})[\s:,-]+(\S.*?)\s*$/.exec(line);
    if (!match) return fill(t('po.exempt.numbersBadLine'), { line });
    const code = match[1]!.toUpperCase();
    if (!US_STATE_NAMES[code]) return fill(t('po.exempt.numbersBadState'), { code });
    if (seen.has(code)) return fill(t('po.exempt.numbersTwice'), { state: stateNameOf(code) });
    seen.add(code);
  }
  return null;
}

/** 🔒 The signer's name and title: a line of text the form's box can hold. A blank clears the key. */
export function exemptionSignerProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') return t('po.exempt.signerNotText');
  return value.trim().length > SIGNER_TEXT_LIMIT ? fill(t('po.exempt.signerTooLong'), { limit: SIGNER_TEXT_LIMIT }) : null;
}

/** 🔒 The default reason is Resale or Manufacturing; a blank clears it back to Resale. */
export function exemptionReasonSettingProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return isOneTimeExemptionReason(value) ? null : t('po.exempt.reasonSetting');
}

/** The form a state's one-time certificate is filled from: the template with a fill map and the committed file it fills. */
export interface OneTimeForm {
  template: CertificateFormTemplate & { fill: CertificateFillMap };
  file: string;
}

/**
 * 🔒 Decision 9600: the state's own committed form whose template carries a fill map, or null. The multistate
 * templates name no state, so a state they alone serve answers null.
 */
export function oneTimeFormFor(stateCode: string | null | undefined): OneTimeForm | null {
  const code = (stateCode ?? '').toUpperCase();
  if (!code) return null;
  const committed = stateExemptionForm(code)?.committed ?? [];
  for (const template of CERTIFICATE_FORM_TEMPLATES) {
    if (template.stateCode !== code || !template.fill) continue;
    const file = committed.find((form) => form.code === template.code);
    if (file) return { template: template as OneTimeForm['template'], file: file.file };
  }
  return null;
}

/**
 * 🔒 Decision 9600: why a One-time Certificate cannot be filled for the ship-to state, in one sentence naming the
 * state, or null where it can. The order still saves and sends with None.
 */
export function oneTimeExemptionProblem(input: { stateCode: string | null | undefined; reason: unknown }): string | null {
  const code = (input.stateCode ?? '').trim().toUpperCase();
  if (!code || !US_STATE_NAMES[code]) return t('po.exempt.noState');
  const state = stateNameOf(code);
  if (NO_SALES_TAX_STATES.includes(code)) return fill(t('po.exempt.noSalesTax'), { state });
  const form = oneTimeFormFor(code);
  if (!form) return fill(t('po.exempt.noForm'), { state });
  if (!isOneTimeExemptionReason(input.reason) || !form.template.fill.reasons[input.reason]) return fill(t('po.exempt.reasonClaims'), { form: form.template.code, state });
  return null;
}

/**
 * 🔒 Decision 9600: what Our Exemption still lacks for this state's form, or null. A reason whose box takes a number
 * needs the state's number, and every form needs the signer's name.
 */
export function oneTimeExemptionSettingsProblem(input: { stateCode: string; reason: OneTimeExemptionReason; numbers: unknown; signerName: unknown }): string | null {
  const form = oneTimeFormFor(input.stateCode);
  const state = stateNameOf(input.stateCode);
  if (!form) return null;
  if (form.template.fill.reasons[input.reason]?.numberField && !exemptionNumberFor(input.numbers, input.stateCode)) {
    return fill(t('po.exempt.noNumber'), { state });
  }
  if (typeof input.signerName !== 'string' || input.signerName.trim() === '') return t('po.exempt.noSigner');
  return null;
}

/** The facts the Purchase Order's form reads for its ship-to: the state, its name, and why a One-time Certificate is refused there. */
export interface PurchaseExemptionFacts {
  stateCode: string | null;
  stateName: string | null;
  formCode: string | null;
  /** The sentence a save of One-time Certificate is refused with here, or null where it fills. */
  refusal: string | null;
  defaultReason: OneTimeExemptionReason;
}
